import type { ParticlePreset, Range } from './presets.js';
import { getSprite, type Sprite } from './sprites.js';

/**
 * Canvas-2D particle engine — pooled, zero-allocation in the rAF loop.
 *
 * Design constraints (the reason this is hand-rolled rather than a library):
 *  - One fixed particle pool per engine, indexed via a free-list. Nothing is
 *    allocated per frame; dead particles are recycled, not GC'd.
 *  - Ambient emitters hold a target live count (respawn on death); burst
 *    emitters spawn a one-shot batch and never respawn.
 *  - Coordinates for emit positions are normalized 0..1 across the element, so
 *    callers don't care about pixel size or DPR.
 *  - Suspends on tab-hidden and on explicit `setPaused` — a background tab burns
 *    no CPU, and reduced-motion is handled by the caller (engine just idles).
 */

export interface EngineOptions {
  /** Backing-store DPR cap (canvas px = css px × min(devicePixelRatio, cap)). */
  dprCap?: number;
  /** Hard ceiling on live particles across all emitters (device budget). */
  maxParticles?: number;
}

export interface AmbientEmitterHandle {
  /** Live target count (respawns to hold this). Intensity-scaled by caller. */
  setTarget(count: number): void;
  /** Remove this emitter; its live particles finish their life then vanish. */
  remove(): void;
}

/** Normalized-space emit origin + optional per-axis normalized spread. */
export interface BurstOptions {
  /** Origin X in 0..1 across the canvas element. */
  x: number;
  /** Origin Y in 0..1. */
  y: number;
  /** Particle count (defaults to the preset's `count`). */
  count?: number;
}

// Struct-of-arrays would be marginally faster, but an object pool reads far more
// clearly and is plenty for these counts. All fields are pre-allocated once.
interface Particle {
  alive: boolean;
  preset: ParticlePreset | null;
  sprite: Sprite | null;
  // Position in CSS px (canvas is scaled by DPR via ctx transform).
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  age: number;
  life: number;
  rot: number;
  spin: number;
  // Turbulence phase offset so particles don't wander in lockstep.
  seed: number;
}

interface AmbientEmitter {
  id: number;
  preset: ParticlePreset;
  sprite: Sprite;
  target: number;
  live: number;
  removed: boolean;
}

function rand(range: Range): number {
  return range[0] + Math.random() * (range[1] - range[0]);
}

export interface ParticleEngine {
  /** Add an ambient emitter that holds `target` live particles. */
  addAmbient(preset: ParticlePreset, target: number): AmbientEmitterHandle;
  /** Fire a one-shot burst at a normalized position. */
  burst(preset: ParticlePreset, opts: BurstOptions): void;
  /** Pause/resume the loop (independent of tab visibility). */
  setPaused(paused: boolean): void;
  /** Update the device budget at runtime (tier change). */
  setMaxParticles(max: number): void;
  /** Current live-particle count (for readouts). */
  count(): number;
  /** Instantaneous FPS estimate (for readouts). */
  fps(): number;
  /** Tear down: cancels rAF, drops listeners, clears the pool. */
  destroy(): void;
}

export function createParticleEngine(
  canvas: HTMLCanvasElement,
  opts: EngineOptions = {},
): ParticleEngine {
  const ctx = canvas.getContext('2d', { alpha: true })!;
  const dprCap = opts.dprCap ?? 1.5;
  let maxParticles = opts.maxParticles ?? 300;

  // ── Particle pool + free-list ──────────────────────────────────────────────
  const POOL_SIZE = 512; // hard cap on simultaneous particles this engine can hold
  const pool: Particle[] = new Array(POOL_SIZE);
  const freeList: number[] = new Array(POOL_SIZE);
  let freeCount = POOL_SIZE;
  let liveCount = 0;
  for (let i = 0; i < POOL_SIZE; i++) {
    pool[i] = {
      alive: false,
      preset: null,
      sprite: null,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      size: 0,
      age: 0,
      life: 0,
      rot: 0,
      spin: 0,
      seed: 0,
    };
    freeList[i] = POOL_SIZE - 1 - i; // pop from the end
  }

  const emitters: AmbientEmitter[] = [];
  let nextEmitterId = 1;

  // ── Sizing ─────────────────────────────────────────────────────────────────
  let cssW = 0;
  let cssH = 0;
  let dpr = 1;

  function resize() {
    const rect = canvas.getBoundingClientRect();
    cssW = rect.width;
    cssH = rect.height;
    dpr = Math.min(window.devicePixelRatio || 1, dprCap);
    const bw = Math.max(1, Math.round(cssW * dpr));
    const bh = Math.max(1, Math.round(cssH * dpr));
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
    }
    // Draw in CSS px; the transform bakes in DPR.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  const ro = new ResizeObserver(resize);
  ro.observe(canvas);
  resize();

  // ── Spawning ───────────────────────────────────────────────────────────────
  function spawn(
    preset: ParticlePreset,
    sprite: Sprite,
    nx: number,
    ny: number,
    emitter: AmbientEmitter | null,
  ): boolean {
    if (freeCount === 0 || liveCount >= maxParticles) return false;
    const idx = freeList[--freeCount]!;
    const p = pool[idx]!;
    p.alive = true;
    p.preset = preset;
    p.sprite = sprite;
    p.x = nx * cssW;
    p.y = ny * cssH;
    p.vx = rand(preset.velocity.x);
    p.vy = rand(preset.velocity.y);
    p.size = rand(preset.size);
    p.age = 0;
    p.life = rand(preset.life);
    p.rot = Math.random() * Math.PI * 2;
    p.spin = preset.spin ? rand(preset.spin) : 0;
    p.seed = Math.random() * 1000;
    liveCount++;
    if (emitter) emitter.live++;
    // Track which emitter owns a respawnable particle via a parallel array.
    ownerOf[idx] = emitter ? emitter.id : 0;
    return true;
  }

  // Parallel to `pool`: which ambient emitter owns each slot (0 = burst/none).
  const ownerOf = new Int32Array(POOL_SIZE);

  function kill(idx: number) {
    const p = pool[idx]!;
    if (!p.alive) return;
    p.alive = false;
    p.preset = null;
    p.sprite = null;
    liveCount--;
    const owner = ownerOf[idx]!;
    if (owner) {
      const em = emitters.find((e) => e.id === owner);
      if (em) em.live--;
    }
    ownerOf[idx] = 0;
    freeList[freeCount++] = idx;
  }

  // ── Ambient emitter respawn: top up each emitter to its target ─────────────
  function topUp() {
    for (const em of emitters) {
      if (em.removed) continue;
      const want = Math.min(em.target, maxParticles);
      while (em.live < want) {
        // Ambient particles seed across the whole element; presets bias their
        // start edge via velocity direction (e.g. embers rise from the bottom).
        const nx = Math.random();
        const ny = seedY(em.preset);
        if (!spawn(em.preset, em.sprite, nx, ny, em)) break;
      }
    }
    // Drop emitters that were removed once their particles have all died.
    for (let i = emitters.length - 1; i >= 0; i--) {
      if (emitters[i]!.removed && emitters[i]!.live <= 0) emitters.splice(i, 1);
    }
  }

  // Where a fresh ambient particle enters, so it doesn't pop in mid-air: rising
  // presets start low, falling presets start high, drifting presets anywhere.
  function seedY(preset: ParticlePreset): number {
    const vy = (preset.velocity.y[0] + preset.velocity.y[1]) / 2;
    if (vy < -4) return 0.85 + Math.random() * 0.25; // rising → below/near bottom
    if (vy > 4) return -0.1 + Math.random() * 0.2; // falling → above/near top
    return Math.random();
  }

  // ── Physics + render ───────────────────────────────────────────────────────
  let lastT = 0;
  let rafId = 0;
  let paused = false;
  let hidden = document.visibilityState === 'hidden';
  let fpsEstimate = 60;

  function frame(t: number) {
    rafId = requestAnimationFrame(frame);
    if (paused || hidden) {
      lastT = t;
      return;
    }
    // Clamp dt so a tab-return or a long stall doesn't teleport particles.
    let dt = lastT ? (t - lastT) / 1000 : 0;
    lastT = t;
    if (dt > 0.05) dt = 0.05;
    if (dt <= 0) return;
    fpsEstimate += (1 / dt - fpsEstimate) * 0.1;

    topUp();
    step(dt, t / 1000);
    render();
  }

  function step(dt: number, tSec: number) {
    for (let i = 0; i < POOL_SIZE; i++) {
      const p = pool[i]!;
      if (!p.alive) continue;
      const preset = p.preset!;
      p.age += dt;
      if (p.age >= p.life) {
        kill(i);
        continue;
      }
      // Gravity.
      if (preset.gravity) p.vy += preset.gravity * dt;
      // Drag (per-second retention → per-frame via pow).
      if (preset.drag !== undefined && preset.drag !== 1) {
        const d = Math.pow(preset.drag, dt * 60);
        p.vx *= d;
        p.vy *= d;
      }
      // Sin-noise turbulence wander (adds to position, not velocity, so it
      // stays bounded and doesn't accumulate energy).
      let tx = 0;
      if (preset.turbulence) {
        const { amplitude, frequency } = preset.turbulence;
        tx = Math.sin(tSec * frequency * Math.PI * 2 + p.seed) * amplitude * dt;
      }
      p.x += p.vx * dt + tx;
      p.y += p.vy * dt;
      if (p.spin) p.rot += p.spin * dt;

      // Cull anything that has wandered well outside the frame (burst debris,
      // stray ambient) so it frees its slot early.
      const m = p.size * 2 + 40;
      if (p.x < -m || p.x > cssW + m || p.y < -m || p.y > cssH + m) {
        // Ambient particles that fall off-frame die and get respawned by topUp;
        // this keeps the bed flowing rather than draining to zero.
        kill(i);
      }
    }
  }

  function render() {
    ctx.clearRect(0, 0, cssW, cssH);
    // Group by blend mode implicitly: set per-particle. Sorting by blend would
    // reduce state changes, but at these counts the switch cost is negligible
    // and sorting would allocate — so we just set it per draw.
    let currentBlend: GlobalCompositeOperation | '' = '';
    for (let i = 0; i < POOL_SIZE; i++) {
      const p = pool[i]!;
      if (!p.alive) continue;
      const preset = p.preset!;
      const sprite = p.sprite!;

      const lifeT = p.age / p.life;
      let alpha = preset.opacity[0] + (preset.opacity[1] - preset.opacity[0]) * lifeT;
      // Blink oscillation (fireflies) modulates alpha on top of the ramp.
      if (preset.blink) {
        alpha *= 0.35 + 0.65 * (0.5 + 0.5 * Math.sin((p.age * preset.blink + p.seed) * Math.PI * 2));
      }
      if (alpha <= 0.003) continue;

      const blend: GlobalCompositeOperation = preset.blend;
      if (blend !== currentBlend) {
        ctx.globalCompositeOperation = blend;
        currentBlend = blend;
      }
      ctx.globalAlpha = alpha;

      const w = p.size;
      // Preserve sprite aspect (wisps/leaves/feathers are non-square).
      const h = (p.size * sprite.height) / sprite.width;

      if (preset.tint) {
        // Tint by drawing the white sprite then multiplying the color over it
        // via a per-particle color rect would be too costly; instead we lean on
        // the fact that the sprite is white and use a cached tinted variant.
        drawTinted(sprite, preset.tint, p, w, h);
      } else if (p.rot !== 0) {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.drawImage(sprite.source, -w / 2, -h / 2, w, h);
        ctx.restore();
      } else {
        ctx.drawImage(sprite.source, p.x - w / 2, p.y - h / 2, w, h);
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  // ── Tinting ────────────────────────────────────────────────────────────────
  // Pre-tint each (sprite, color) pair once into an offscreen canvas so the hot
  // loop is a plain drawImage. Keyed by sprite source + tint.
  const tintCache = new Map<string, HTMLCanvasElement>();

  function tintedFor(sprite: Sprite, tint: [number, number, number]): HTMLCanvasElement {
    const key = `${sprite.width}x${sprite.height}:${tint[0]},${tint[1]},${tint[2]}`;
    const hit = tintCache.get(key);
    if (hit) return hit;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(sprite.width));
    c.height = Math.max(1, Math.round(sprite.height));
    const cx = c.getContext('2d')!;
    cx.drawImage(sprite.source, 0, 0, c.width, c.height);
    // Multiply the tint through, preserving the sprite's alpha shape.
    cx.globalCompositeOperation = 'source-in';
    cx.fillStyle = `rgb(${tint[0]},${tint[1]},${tint[2]})`;
    cx.fillRect(0, 0, c.width, c.height);
    tintCache.set(key, c);
    return c;
  }

  function drawTinted(
    sprite: Sprite,
    tint: [number, number, number],
    p: Particle,
    w: number,
    h: number,
  ) {
    const tinted = tintedFor(sprite, tint);
    if (p.rot !== 0) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.drawImage(tinted, -w / 2, -h / 2, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(tinted, p.x - w / 2, p.y - h / 2, w, h);
    }
  }

  // ── Visibility ─────────────────────────────────────────────────────────────
  function onVisibility() {
    hidden = document.visibilityState === 'hidden';
    if (!hidden) lastT = 0; // reset dt baseline on resume
  }
  document.addEventListener('visibilitychange', onVisibility);

  rafId = requestAnimationFrame(frame);

  // ── Public API ─────────────────────────────────────────────────────────────
  return {
    addAmbient(preset, target) {
      const sprite = getSprite(preset.sprite);
      const em: AmbientEmitter = {
        id: nextEmitterId++,
        preset,
        sprite,
        target: Math.max(0, Math.floor(target)),
        live: 0,
        removed: false,
      };
      emitters.push(em);
      return {
        setTarget(count: number) {
          em.target = Math.max(0, Math.floor(count));
        },
        remove() {
          em.removed = true;
          em.target = 0;
        },
      };
    },

    burst(preset, o) {
      const sprite = getSprite(preset.sprite);
      const n = o.count ?? preset.count;
      for (let i = 0; i < n; i++) {
        if (!spawn(preset, sprite, o.x, o.y, null)) break;
      }
    },

    setPaused(p) {
      paused = p;
      if (!p) lastT = 0;
    },

    setMaxParticles(max) {
      maxParticles = Math.max(0, Math.floor(max));
    },

    count() {
      return liveCount;
    },

    fps() {
      return Math.round(fpsEstimate);
    },

    destroy() {
      cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', onVisibility);
      ro.disconnect();
      for (let i = 0; i < POOL_SIZE; i++) pool[i]!.alive = false;
      liveCount = 0;
      emitters.length = 0;
      tintCache.clear();
      ctx.clearRect(0, 0, cssW, cssH);
    },
  };
}
